import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Building2, FileWarning, BadgeCheck, Lock } from "lucide-react";
import { API, fmtScore, licenseLabel, fmtDate } from "./shared";
import { RiskPill } from "./RiskPill";
import { OverallRiskBanner, PillarHeatMap, DiagnosticHistory } from "./ExecutiveClarity";
import { SchoolHub } from "./SchoolHub";

const Stat = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
  </div>
);

export default function SchoolDrillDown() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [pid, setPid] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/portfolio/schools/${tenantId}/drill-down`, { credentials: "include" });
        if (r.status === 403) { setDenied(true); return; }
        if (r.ok) setData(await r.json());
        const meR = await fetch(`${API}/api/portfolio/me`, { credentials: "include" });
        if (meR.ok) {
          const me = await meR.json();
          const list = me.portfolios || [];
          let found = new URLSearchParams(window.location.search).get("pid");
          // Admin preview: pick the portfolio that actually has this school in a period.
          for (const p of found ? [] : list) {
            const r = await fetch(`${API}/api/portfolio/${p.id}/periods`, { credentials: "include" });
            const j = r.ok ? await r.json() : {};
            if ((j.periods || []).some((x) => (x.requests || []).some((q) => q.tenant_id === tenantId))) { found = p.id; break; }
          }
          setPid(found || list[0]?.id || null);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  if (denied) {
    return (
      <div data-testid="drill-down-denied" className="mx-auto mt-16 max-w-md rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
        <Lock className="mx-auto h-10 w-10 text-red-400" />
        <h2 className="mt-4 text-lg font-semibold text-red-700">Access Denied</h2>
        <p className="mt-2 text-sm text-red-600">This school is not part of your assigned portfolio.</p>
        <button onClick={() => navigate("/portfolio/dashboard")} className="mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">Back to Dashboard</button>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-center text-sm text-slate-400">School not found.</div>;

  return (
    <div data-testid="drill-down-page" className="space-y-6">
      <button onClick={() => navigate("/portfolio/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Back to Portfolio
      </button>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-400"><Building2 className="h-4 w-4" /><span className="text-xs uppercase tracking-wide">Read-Only School Summary</span></div>
          <h1 data-testid="drill-down-school-name" className="mt-1 text-2xl font-semibold text-slate-900">{data.school_name}</h1>
          <p className="mt-0.5 text-sm text-slate-500">{data.latest_period || "No completed diagnostic"} · Updated {fmtDate(data.last_updated)}{data.scoring_version && <span data-testid="drill-down-scoring-version"> · Scoring v{data.scoring_version}</span>}</p>
        </div>
        <RiskPill level={data.risk_level} size="lg" />
      </div>

      <SchoolHub pid={pid} tenantId={tenantId} />

      {!data.has_diagnostic ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No completed Diagnostic available for this school yet.
        </div>
      ) : (
        <>
          <div className="my-3">
            <OverallRiskBanner score={data.score} riskBand={data.overall_risk_band} provisional={!data.assessment_is_final} testid="drill-down-overall-risk" />
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Overall Score" value={data.score != null && !data.assessment_is_final ? `${fmtScore(data.score)} · Provisional` : fmtScore(data.score)} testid="drill-down-score" />
            <Stat label="Completion" value={`${data.completion_pct}%`} testid="drill-down-completion" />
            <Stat label="Evidence Complete" value={`${data.evidence_completion_pct}%`} sub={`${data.missing_evidence_count} missing`} testid="drill-down-evidence-gaps" />
            <Stat label="License" value={licenseLabel(data.license_status)} testid="drill-down-license-status" />
          </div>

          <div className="pt-2"><PillarHeatMap pillars={data.pillars} onReview={(p) => navigate(`/portfolio/schools/${tenantId}/diagnostic?pillar=${p.id}${data.diagnostic_id ? `&diag=${data.diagnostic_id}` : ""}`)} /></div>

          {/* "School vs Portfolio — gap over time" folded into the history chart; its testids live on this wrapper. */}
          <div className="pt-2" data-testid="gap-trend"><div data-testid={`gap-trend-school-${tenantId}`}><DiagnosticHistory history={data.history} tenantId={tenantId} /></div></div>
        </>
      )}

    </div>
  );
}
